// TES-1245 (TES-1240 S5, A-02 "KÖER OCH ÖPPNA/HANTERA" 2026-10-13):
// "I arbetsköer ska Hantera öppna rätt arbetsmoment direkt i det steg där läkaren kan utföra åtgärden. Öppna patientöversikt ska vara en separat sekundär handling för att läsa bredare patientinformation. Två knappar som leder till samma patientvy får inte visas."
//
// A work-queue row used to offer "Hantera" (expand the card) and a generic
// "Öppna" (link to `ctaPath`), and the expanded card a third link,
// "Öppna patientbilden", to the very same `ctaPath`. This module decides,
// per item kind, what the ONE primary action is called and where it lands,
// and whether the secondary patient-overview link may be shown beside it.
//
// Pure on purpose: no router, no React, no path aliases, so the admin's
// `node --test` suite runs it as is.

import type {
  Role,
  WorkQueueItem,
  WorkQueueItemKind,
} from "@care-by-ella/contracts";
import { COACH_WELCOME_CTA } from "@care-by-ella/contracts";
import { TAB_PARAM } from "./patient-links";

/**
 * The secondary action's label, in every admin queue. TES-1400 (Ö-530 /
 * Ö-568, Emelie 2026-10-15): "Sekundär länk heter Öppna patientbilden.
 * Primärknappen ska namnge och öppna eller lösa den konkreta uppgiften."
 */
export const PATIENT_OVERVIEW_LABEL = "Öppna patientbilden";

/**
 * The patient's "Översikt" tab. `?flik=oversikt` is explicit: a transfer
 * patient opened without a tab is sent on to "overtag" (transferLandingTab),
 * which would make this link land on the same view as "Granska receptövertaget".
 */
export function patientOverviewHref(patientId: string): string {
  return `/patients/${encodeURIComponent(patientId)}?${TAB_PARAM}=oversikt`;
}

/** The primary action of a row: expand the work step in the card itself,
 *  or go to the page where it is performed. */
export type WorkItemPrimaryCta =
  | { label: string; how: "expand" }
  | { label: string; how: "link"; href: string };

// The concrete name of each kind's work step.
const LABEL: Record<WorkQueueItemKind, string> = {
  handoff: "Granska överlämningen",
  follow_up: "Hantera uppföljningen",
  checkin: "Svara på incheckningen",
  lab_order: "Öppna blodprovet",
  needs_follow_up_contact: "Skicka en varm fråga",
  coach_welcome: COACH_WELCOME_CTA,
  renewal: "Granska förnyelsen",
  onboarding: "Lämna svar och fatta beslut",
  side_effect: "Granska biverkningarna",
  medical_alert: "Hantera larmet",
  medical_review: "Granska det medicinska ärendet",
  webdoc_confirmation: "Bekräfta införd i Webdoc",
  // A-02 "PASSERADE MÖTEN": the row's own action keeps the name "Hantera".
  meeting_documentation: "Hantera mötet",
  // TES-1240, verbatim.
  completion_round: "Granska kompletteringen",
  // TES-1332 — rendered by DoctorAnswerCard, which owns its own label.
  doctor_question_answer: "Hantera svar",
  // TES-1203 — her own withdrawal, read on the overview's medical-case panel.
  case_withdrawal: "Öppna det medicinska ärendet",
  // TES-1554 — admin's task, on "Avslut och återbetalning".
  subscription_end_refund: "Avsluta och återbetala",
  // Unreachable since TES-764/TES-818 (no role runs their lanes).
  missed_check_in: "Hantera ärendet",
  missed_weight_log: "Hantera ärendet",
  program_change: "Hantera ärendet",
};

// Kinds whose work step IS the expanded card: a composer, or a confirmation
// that only exists on the row. Everything else is decided on the patient's
// page, and `ctaPath` deep-links to that step.
const EXPANDS: ReadonlySet<WorkQueueItemKind> = new Set([
  "follow_up",
  "needs_follow_up_contact",
  "webdoc_confirmation",
  "doctor_question_answer",
  "missed_check_in",
  "missed_weight_log",
  "program_change",
]);

function tabOf(href: string): string | null {
  const query = href.split("#")[0]!.split("?")[1] ?? "";
  return new URLSearchParams(query).get(TAB_PARAM);
}

export function workItemPrimaryCta(
  item: Pick<
    WorkQueueItem,
    "kind" | "ctaPath" | "priority" | "labResultCheck" | "journalParentKind"
  >,
  role: Role | undefined,
): WorkItemPrimaryCta {
  const link = (label: string): WorkItemPrimaryCta => ({
    label,
    how: "link",
    href: item.ctaPath,
  });
  switch (item.kind) {
    // The coach answers in the card; the doctor reads the check-in on the
    // patient's "Veckor" tab, where it is marked as reviewed.
    case "checkin":
      return role === "coach"
        ? { label: LABEL.checkin, how: "expand" }
        : link("Granska incheckningen");
    // A result to review is "Granska provsvaret"; a waiting order or a
    // Webdoc check (whose choices sit on the row) only opens the tab.
    case "lab_order":
      return link(
        item.priority === "high" && !item.labResultCheck
          ? "Granska provsvaret"
          : LABEL.lab_order,
      );
    // A transfer patient's onboarding row is decided in her review.
    case "onboarding":
      return link(
        tabOf(item.ctaPath) === "overtag"
          ? "Granska receptövertaget"
          : LABEL.onboarding,
      );
    // TES-1579 — a meeting's receipt is confirmed on the row; every other
    // journal note's is ticked in the note's own Webdoc step.
    case "webdoc_confirmation":
      return item.journalParentKind
        ? link(LABEL.webdoc_confirmation)
        : { label: LABEL.webdoc_confirmation, how: "expand" };
    default:
      return EXPANDS.has(item.kind)
        ? { label: LABEL[item.kind], how: "expand" }
        : link(LABEL[item.kind]);
  }
}

/**
 * Does `href` open the patient's "Översikt"? Only a `/patients/:id` URL with
 * no other path segment, and no tab or `?flik=oversikt`. The hash is an
 * anchor INSIDE the view, not another view.
 */
export function opensPatientOverview(href: string): boolean {
  const path = href.split("#")[0]!.split("?")[0]!;
  if (!/^\/patients\/[^/]+$/.test(path)) return false;
  const tab = tabOf(href);
  return tab === null || tab === "oversikt";
}

/**
 * The secondary "Öppna patientbilden" link, or null when the primary
 * already goes to the overview — two buttons to the same view are what A-02
 * rules out.
 */
export function patientOverviewLinkFor(
  patientId: string,
  primary: WorkItemPrimaryCta,
): string | null {
  if (primary.how === "link" && opensPatientOverview(primary.href)) return null;
  return patientOverviewHref(patientId);
}
